import { useState, useEffect } from "react";
import "./QuarterlyReview.css";

const categoryQuarters = {
  Work: {
    goals: ["Launch MVP product", "Secure funding", "Improve customer satisfaction", "Scale infrastructure", "Build team"],
    achievements: ["Product release", "Investor pitch completed", "Revenue milestone", "Customer acquisition", "Team expansion"],
    lessons: ["Communication crucial", "Technical debt management", "Resource planning", "Agile methodology benefits", "Scalability importance"]
  },
  Learning: {
    goals: ["Become full-stack developer", "Master system design", "Build 3 major projects", "Complete certification", "Contribute to open source"],
    achievements: ["All courses completed", "Projects deployed", "Certification earned", "Skills mastered", "Open source contributions"],
    lessons: ["Consistency matters", "Practical application helps", "Community learning valuable", "Documentation important", "Hands-on building critical"]
  },
  SelfDevelopment: {
    goals: ["Develop leadership skills", "Achieve work-life balance", "Build strong network", "Establish daily habits", "Improve communication"],
    achievements: ["Led team project", "Reduced overtime", "Made meaningful connections", "30-day streak", "Gave presentation"],
    lessons: ["Boundaries essential", "Growth requires effort", "Relationships matter", "Consistency builds habits", "Self-awareness key"]
  },
  Fitness: {
    goals: ["Complete half marathon", "Achieve fitness milestones", "Build muscle consistently", "Maintain healthy weight", "Improve performance"],
    achievements: ["Finished race", "Personal records set", "Weight loss achieved", "Strength increased", "Endurance improved"],
    lessons: ["Nutrition + training critical", "Recovery important", "Consistency over intensity", "Progressive overload works", "Discipline required"]
  }
};

export default function QuarterlyReview() {
  const [review, setReview] = useState({
    category: "Work",
    quarter: "",
    goals: "",
    achievements: "",
    metrics: "",
    lessons: "",
    roadmap: ""
  });
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState("All");

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    fetch("http://localhost:5000/api/quarterly", {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setReviews(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReview({ ...review, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setReview({ ...review, category, goals: "", achievements: "", lessons: "", roadmap: "" });
  };

  const handleSubmit = async () => {
    if (!review.quarter || !review.goals) {
      alert("Please fill in required fields (Quarter, Goals)");
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch("http://localhost:5000/api/quarterly", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(review),
      });
      const data = await res.json();
      setReviews([...reviews, data]);
      setReview({ category: "Work", quarter: "", goals: "", achievements: "", metrics: "", lessons: "", roadmap: "" });
      setShowForm(false);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredReviews = filterCategory === "All" ? reviews : reviews.filter(r => r.category === filterCategory);

  return (
    <div className="quarterly-review-container">
      <div className="quarterly-header">
        <h2>📈 Quarterly Review</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ New Review"}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Create Quarterly Review</h3>

          {/* Category Selection */}
          <div className="form-group">
            <label>Category *</label>
            <div className="category-buttons">
              {Object.keys(categoryQuarters).map(cat => (
                <button
                  key={cat}
                  className={`category-btn ${review.category === cat ? "active" : ""}`}
                  onClick={() => handleCategoryChange({ target: { value: cat } })}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Quarter *</label>
              <select name="quarter" value={review.quarter} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="Q1">Q1 (Jan-Mar)</option>
                <option value="Q2">Q2 (Apr-Jun)</option>
                <option value="Q3">Q3 (Jul-Sep)</option>
                <option value="Q4">Q4 (Oct-Dec)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Year</label>
              <input type="number" name="year" min="2020" max="2030" placeholder="2026" defaultValue={new Date().getFullYear()} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Goals *</label>
              <select name="goals" value={review.goals} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryQuarters[review.category].goals.map(goal => (
                  <option key={goal} value={goal}>{goal}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Achievements</label>
              <select name="achievements" value={review.achievements} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryQuarters[review.category].achievements.map(ach => (
                  <option key={ach} value={ach}>{ach}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Key Metrics</label>
              <input
                type="text"
                name="metrics"
                value={review.metrics}
                onChange={handleChange}
                placeholder="e.g., Revenue growth 20%, Users 5000, Retention 90%"
              />
            </div>
            <div className="form-group">
              <label>Lessons Learned</label>
              <select name="lessons" value={review.lessons} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryQuarters[review.category].lessons.map(lesson => (
                  <option key={lesson} value={lesson}>{lesson}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Roadmap for Next Quarter</label>
            <textarea
              name="roadmap"
              value={review.roadmap}
              onChange={handleChange}
              placeholder="What's your plan for the next quarter?"
              rows="4"
            />
          </div>

          <button className="btn-submit" onClick={handleSubmit}>Save Review</button>
        </div>
      )}

      <div className="filter-section">
        <label>Filter by Category:</label>
        <div className="filter-buttons">
          {["All", "Work", "Learning", "SelfDevelopment", "Fitness"].map(cat => (
            <button
              key={cat}
              className={`filter-btn ${filterCategory === cat ? "active" : ""}`}
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="reviews-section">
        <h3>Quarterly Reviews ({filteredReviews.length})</h3>
        {filteredReviews.length === 0 ? (
          <p className="empty-state">No quarterly reviews yet. Create your first quarterly review!</p>
        ) : (
          <div className="reviews-grid">
            {filteredReviews.map((r, i) => (
              <div key={i} className={`review-card category-${r.category?.toLowerCase() || "work"}`}>
                <div className="review-header">
                  <span className="badge">{r.category || "Work"}</span>
                  <span className="quarter-label">{r.quarter}</span>
                </div>
                <h4>{r.goals}</h4>
                <div className="review-details">
                  {r.achievements && <p><strong>✓ Achievements:</strong> {r.achievements}</p>}
                  {r.metrics && <p><strong>📊 Metrics:</strong> {r.metrics}</p>}
                  {r.lessons && <p><strong>💡 Lessons:</strong> {r.lessons}</p>}
                  {r.roadmap && <p><strong>🗺️ Roadmap:</strong> {r.roadmap}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}